import React, { useState, useRef, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAdminStore } from '../../stores/useAdminStore';
import { useSeasonsStore } from '../../stores/useSeasonsStore';
import { useUIStore } from '../../stores/useUIStore';
import { BaseModal } from '../shared/Modal/BaseModal';
import Button from '../ui/Button/Button';
import { DeleteConfirmation } from './DeleteConfirmation';
import './SeasonActionsModal.css';

// seasonService.deleteSeason rejects a non-empty season with "...: it has N event(s). ..."
const SEASON_NOT_EMPTY_MESSAGE_PATTERN = /it has \d+ event/;
export const SEASON_NOT_EMPTY_TOAST = 'Cannot delete season: it contains events. Please delete all events first.';

export const SeasonActionsModal: React.FC = () => {
  const {
    showSeasonActionsModal,
    selectedSeasonForActions,
    closeSeasonActionsModal
  } = useAdminStore(
    useShallow(s => ({
      showSeasonActionsModal: s.showSeasonActionsModal,
      selectedSeasonForActions: s.selectedSeasonForActions,
      closeSeasonActionsModal: s.closeSeasonActionsModal,
    }))
  );
  const { deleteSeason, isLoading } = useSeasonsStore();
  const { showToast } = useUIStore();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  // Simplified useEffect - only handle modal opening/closing
  useEffect(() => {
    if (selectedSeasonForActions && showSeasonActionsModal) {
      setShowDeleteConfirmation(false);
    }
  }, [selectedSeasonForActions, showSeasonActionsModal]);

  if (!showSeasonActionsModal || !selectedSeasonForActions) {
    return null;
  }


  // Enhanced handleDelete with event validation
  const handleDelete = async () => {
    try {
      // Backend will validate that season has no events before deletion
      await deleteSeason(selectedSeasonForActions.seasonId);
      showToast('Season deleted successfully', 'success');
      closeSeasonActionsModal();
    } catch (error) {
      const errorMessage = (error as Error).message;
      if (SEASON_NOT_EMPTY_MESSAGE_PATTERN.test(errorMessage)) {
        showToast(SEASON_NOT_EMPTY_TOAST, 'error');
      } else {
        showToast(errorMessage, 'error');
      }
    }
  };

  return (
    <BaseModal
      isOpen={showSeasonActionsModal}
      onClose={closeSeasonActionsModal}
      title="Delete Season"
      className="season-actions-modal"
      initialFocusRef={deleteButtonRef}
      data-testid="season-actions-modal"
    >
      {!showDeleteConfirmation ? (
        <div className="season-delete-info">
          <div className="season-details">
            <h4>Season Information</h4>
            <p><strong>Name:</strong> {selectedSeasonForActions.seasonName}</p>
            <p><strong>Year:</strong> {selectedSeasonForActions.year}</p>
          </div>

          <div className="delete-warning">
            <p><strong>Warning:</strong> This will permanently delete the season. The season can only be deleted if it has no associated events.</p>
          </div>

          <div className="form-actions">
            <Button size="action"
              ref={deleteButtonRef}
              variant="danger"
              onClick={() => setShowDeleteConfirmation(true)}
              disabled={isLoading}
              data-testid="delete-season-button"
            >
              Delete Season
            </Button>
          </div>
        </div>
      ) : (
        <DeleteConfirmation
          prompt={<>Are you sure you want to delete the season "{selectedSeasonForActions.seasonName} {selectedSeasonForActions.year}"?</>}
          warning="This action cannot be undone. This will only succeed if the season has no associated events."
          confirmLabel="Yes, Delete Season"
          isDeleting={isLoading}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirmation(false)}
          confirmTestId="confirm-delete-season-button"
        />
      )}
    </BaseModal>
  );
};
